import _ from "lodash";
import {
	ICH_MILESTONE_EVENTS,
	applyIchMilestoneMirrors,
	getIchActivityKey,
	getIchMilestoneByKey,
	isIchMilestoneReached,
	readIchMilestoneValues,
	stampIchMilestones
} from "./ichMilestones";
import {
	assertProjectOpsAddendumEditable,
	CUSTOM_HOME_PROJECT_TEMPLATE,
	DEFAULT_CUSTOM_HOME_ACTIVITY_IDS,
	PROJECT_OPS_PROJECT_WORK_ITEM_TYPE,
	PROJECT_OPS_TASK_WORK_ITEM_TYPE,
	buildProjectOpsLaunchContext,
	buildProjectOpsProjectContext,
	buildProjectOpsStageContext,
	getProjectOpsFromWorkItem,
	normalizeProjectOpsBudgetTargets,
	normalizeProjectOpsTemplate,
	renderProjectOpsTemplate
} from "./projectOpsUtils";

/**
 * Turns the events the platform can prove (a locked Agreement of Sale, a locked
 * addendum, a submitted contributor session) into milestone stamps on the ICH
 * contract anchor, and plans how the Project Ops work items should advance so the
 * Work Queue never has to be touched by hand for those steps.
 */

export function getIchContractEvents(pricingContractProto = null, context = {}) {
	if (!pricingContractProto || !Array.isArray(pricingContractProto.documents)) {
		return [];
	}
	return pricingContractProto.documents
		.filter((document) => document && document.status === "locked")
		.map((document) => {
			const at = document.lockedAt || context.at || new Date();
			if (document.kind === "initialPurchase") {
				return { type: ICH_MILESTONE_EVENTS.AGREEMENT_OF_SALE_LOCKED, at };
			}
			if (document.kind === "addendum") {
				const number = Number(document.number);
				return Number.isFinite(number) && number > 0
					? { type: ICH_MILESTONE_EVENTS.ADDENDUM_LOCKED, number, at }
					: null;
			}
			return null;
		})
		.filter(Boolean);
}

export function getIchSessionEvent(context = {}) {
	if (!context.submitted) {
		return null;
	}
	const activityKey = getIchActivityKey(context.activityId, context.activityName);
	if (!activityKey) {
		return null;
	}
	return { type: ICH_MILESTONE_EVENTS.SESSION_SUBMITTED, activityKey, at: context.at || new Date() };
}

/**
 * context: { at?, submitted?, activityId?, activityName? } describing the save that
 * is about to happen. Returns a new instance with every provable milestone stamped
 * and the root mirrors refreshed. Safe to call on every save: stamping never
 * overwrites a milestone that is already reached.
 */
export function applyIchContractMilestones(instance, context = {}) {
	if (!instance || typeof instance !== "object") {
		return instance;
	}
	const events = getIchContractEvents(instance.pricingContractProto, context);
	const sessionEvent = getIchSessionEvent(context);
	if (sessionEvent) {
		events.push(sessionEvent);
	}
	let next = instance;
	events.forEach((event) => {
		next = stampIchMilestones(next, event);
	});
	return applyIchMilestoneMirrors(next);
}

export function getNewlyReachedIchMilestones(before, after) {
	const previous = readIchMilestoneValues(before || {});
	const current = readIchMilestoneValues(after || {});
	return Object.keys(current).filter((key) => {
		const milestone = getIchMilestoneByKey(key);
		return isIchMilestoneReached(milestone, current[key]) && !isIchMilestoneReached(milestone, previous[key]);
	});
}

export function getIchStageActivityId(stage = {}) {
	if (!stage || !stage.activityIdStateField) {
		return "";
	}
	return _.toString(stage.defaultActivityId || (stage.activityIdKey ? DEFAULT_CUSTOM_HOME_ACTIVITY_IDS[stage.activityIdKey] : "") || "").trim();
}

function buildUserRecipients(userSubId, role) {
	const normalized = _.toString(userSubId || "").trim();
	return normalized ? [{ userSubId: normalized, role, recipientType: "assignee" }] : [];
}

function buildStageActivitySnapshot(activityId, stage = {}) {
	if (!activityId) {
		return null;
	}
	const snapshot = { id: activityId, name: stage.activityName || stage.label || activityId };
	if (stage.description) {
		snapshot.description = stage.description;
	}
	return snapshot;
}

/**
 * The same work items Activity Ops creates for a new project, with its defaults
 * filled in: owner = the given user, every stage assigned to the owner, no due
 * dates, the template's default budget targets, no notifications on create.
 * Returns { projectOps, payloads } where payloads[0] is the project itself and
 * the rest follow the template's stage order.
 */
export function buildIchHomeWorkItemPayloads(options = {}) {
	const orgId = _.toString(options.orgId || "").trim();
	const ownerUserSubId = _.toString(options.ownerUserSubId || "").trim();
	const template = normalizeProjectOpsTemplate(options.template || CUSTOM_HOME_PROJECT_TEMPLATE);
	const projectOps = buildProjectOpsProjectContext({
		template,
		projectName: options.projectName,
		customerName: options.customerName,
		customerEmail: options.customerEmail,
		customerPhone: options.customerPhone
	});
	const projectId = projectOps.projectId;
	const customerName = projectOps.customer && projectOps.customer.name ? projectOps.customer.name : "";
	const project = {
		orgId,
		type: PROJECT_OPS_PROJECT_WORK_ITEM_TYPE,
		status: "open",
		priority: "normal",
		title: `Project: ${projectOps.projectName}`,
		description: customerName ? `Customer: ${customerName}` : "Project orchestration parent.",
		sourceType: PROJECT_OPS_PROJECT_WORK_ITEM_TYPE,
		sourceId: projectId,
		dedupeKey: `project_ops:${orgId}:${projectId}`,
		recipients: buildUserRecipients(ownerUserSubId, "project-owner"),
		notifyOnCreate: false,
		metadata: {
			projectOps,
			launchContext: buildProjectOpsLaunchContext(projectOps),
			createdFrom: "ichHomesDashboard",
			templateKey: template.key,
			template
		}
	};
	const stages = (template.stages || []).map((stage) => {
		const budgetTargets = normalizeProjectOpsBudgetTargets(stage.defaultBudgetTargets || []);
		const stageProjectOps = buildProjectOpsStageContext(projectOps, stage, {
			budgetTargetMode: budgetTargets.length > 1 ? "multi" : budgetTargets.length === 1 ? "single" : "",
			budgetTargets
		});
		const activityId = getIchStageActivityId(stage);
		const initialStatus = stage.initialStatus || "open";
		const role = stage.activityRole || stage.key || "assignee";
		return {
			orgId,
			type: stage.workItemType || PROJECT_OPS_TASK_WORK_ITEM_TYPE,
			status: initialStatus,
			priority: stage.priority || "normal",
			title: renderProjectOpsTemplate(stage.titleTemplate || `${stage.label} - {{projectName}}`, {
				projectName: projectOps.projectName,
				customerName,
				stageLabel: stage.label,
				addendumNumber: stage.addendumNumber
			}),
			description: stage.description || "",
			activityId: activityId || null,
			sourceType: PROJECT_OPS_PROJECT_WORK_ITEM_TYPE,
			sourceId: projectId,
			dueAt: null,
			dedupeKey: `project_ops:${orgId}:${projectId}:${stage.key}`,
			recipients: buildUserRecipients(ownerUserSubId, role),
			notifyOnCreate: false,
			metadata: {
				projectOps: stageProjectOps,
				activitySnapshot: buildStageActivitySnapshot(activityId, stage),
				launchContext: buildProjectOpsLaunchContext(stageProjectOps, {
					activityName: stage.activityName || stage.label,
					activityOpsRole: stage.activityRole || stage.key || "",
					recipientRole: stage.activityRole || stage.key || "",
					openMode: "resume_or_create",
					canEdit: true,
					readOnly: false,
					lifecycle: {
						openMode: "resume_or_create",
						canEdit: true,
						readOnly: false
					},
					projectOpsProjectionConfig: stage.projection || null
				}),
				parentProjectId: projectId,
				parentProjectWorkItemType: PROJECT_OPS_PROJECT_WORK_ITEM_TYPE,
				templateStageKey: stage.key,
				milestoneKey: stage.milestoneKey || "",
				assignedUserSubId: ownerUserSubId,
				projectOpsProjectionConfig: stage.projection || null
			}
		};
	});
	return { projectOps, template, payloads: [project].concat(stages) };
}

/**
 * A stage whose session prices an addendum against the locked contract
 * (artifactType addendum / contractMode draft_addendum / addendumNumber > 0).
 */
export function isIchAddendumStage(workItem = {}) {
	const projectOps = getProjectOpsFromWorkItem(workItem);
	return projectOps.artifactType === "addendum"
		|| _.toString(projectOps.contractMode || "").toLowerCase().indexOf("addendum") !== -1
		|| Number(projectOps.addendumNumber) > 0;
}

export function getIchDraftAddendumDocument(pricingContractProto = null) {
	const documents = pricingContractProto && Array.isArray(pricingContractProto.documents) ? pricingContractProto.documents : [];
	return documents.find((document) => document && document.kind === "addendum" && document.status === "draft") || null;
}

/**
 * What the pricer's "Start Addendum" does, without the pricer: reuse the draft
 * that already exists, or add Addendum #N (N = addendums so far + 1) as a copy
 * of the effective locked document and make it the active document. Returns
 * null when there is no locked contract to build on. Never mutates the input.
 */
export function buildIchDraftAddendumProto(pricingContractProto = null, options = {}) {
	if (!pricingContractProto || !Array.isArray(pricingContractProto.documents) || pricingContractProto.documents.length === 0) {
		return null;
	}
	const stageContext = options.projectOps || {};
	const strict = Number(stageContext.structuralAddendumLimit) > 0;
	const next = _.cloneDeep(pricingContractProto);
	// Only an addendum draft counts; an unlocked initial purchase is not one.
	const existingDraft = next.documents.find((document) => document && document.kind === "addendum" && document.status === "draft");
	if (existingDraft) {
		if (strict) assertProjectOpsAddendumEditable(next, stageContext);
		const activated = next.activeDocumentId !== existingDraft.id;
		next.activeDocumentId = existingDraft.id;
		return { proto: next, document: existingDraft, created: false, changed: activated };
	}
	const effective = next.documents.find((document) => document && document.id === next.effectiveDocumentId)
		|| next.documents[next.documents.length - 1];
	if (!effective || effective.status !== "locked") {
		return null;
	}
	const number = next.documents.filter((document) => document && document.kind === "addendum").length + 1;
	const document = {
		id: `addendum-${number}`,
		kind: "addendum",
		number,
		title: (stageContext.addendumTitles || {})[number] || `Addendum #${number}`,
		status: "draft",
		activeBudgetLineId: "",
		baseDocumentId: effective.id,
		createdAt: (options.now || new Date()).toISOString(),
		invoiceItems: _.cloneDeep(Array.isArray(effective.invoiceItems) ? effective.invoiceItems : []),
		jobDetails: _.cloneDeep(_.isPlainObject(effective.jobDetails) ? effective.jobDetails : {})
	};
	next.documents.push(document);
	next.activeDocumentId = document.id;
	if (strict) assertProjectOpsAddendumEditable(next, stageContext);
	return { proto: next, document, created: true, changed: true };
}

/**
 * What the pricer's "Lock Agreement of Sale" does, without the pricer: freeze the
 * home's current priced items as the locked Initial Purchase (creating the
 * contract when none exists, or locking its draft). Returns null when there is
 * nothing to lock: no priced items, or the active document is not the initial
 * purchase draft (addendums are locked in the pricer). Never mutates the input.
 */
export function buildIchLockedContractProto(instance = {}, options = {}) {
	if (!_.isPlainObject(instance)) {
		return null;
	}
	const now = (options.now || new Date()).toISOString();
	const invoiceItems = Array.isArray(instance.invoiceItems) ? _.cloneDeep(instance.invoiceItems) : [];
	const jobDetails = _.isPlainObject(instance.jobDetails) ? _.cloneDeep(instance.jobDetails) : {};
	const existing = _.isPlainObject(instance.pricingContractProto) && Array.isArray(instance.pricingContractProto.documents) && instance.pricingContractProto.documents.length > 0
		? instance.pricingContractProto
		: null;

	if (!existing) {
		if (invoiceItems.length === 0) {
			return null;
		}
		const initialPurchase = {
			id: "initial-purchase",
			kind: "initialPurchase",
			number: 0,
			title: "Initial Purchase",
			status: "locked",
			activeBudgetLineId: "",
			createdAt: now,
			lockedAt: now,
			invoiceItems,
			jobDetails
		};
		return {
			proto: {
				schemaVersion: 1,
				sourceEstimateValue: _.toString(instance.selectedValue || ""),
				effectiveDocumentId: initialPurchase.id,
				activeDocumentId: initialPurchase.id,
				documents: [initialPurchase]
			},
			document: initialPurchase,
			created: true
		};
	}

	const active = existing.documents.find((document) => document && document.id === existing.activeDocumentId) || existing.documents[0];
	if (!active || active.kind !== "initialPurchase" || active.status !== "draft") {
		return null;
	}
	const next = _.cloneDeep(existing);
	next.documents = next.documents.map((document) => (
		document && document.id === active.id
			? { ...document, status: "locked", activeBudgetLineId: "", lockedAt: now, invoiceItems: invoiceItems.length ? invoiceItems : document.invoiceItems, jobDetails }
			: document
	));
	next.effectiveDocumentId = active.id;
	next.activeDocumentId = active.id;
	return {
		proto: next,
		document: next.documents.find((document) => document && document.id === active.id),
		created: false
	};
}

function getWorkItemStageKey(workItem = {}) {
	return _.toString(
		_.get(workItem, ["metadata", "templateStageKey"])
		|| _.get(getProjectOpsFromWorkItem(workItem), "stageKey")
		|| ""
	).trim();
}

function getWorkItemProjectId(workItem = {}) {
	return _.toString(_.get(getProjectOpsFromWorkItem(workItem), "projectId") || _.get(workItem, ["metadata", "parentProjectId"]) || "").trim();
}

const TERMINAL_WORK_ITEM_STATUSES = new Set(["completed", "cancelled"]);

/**
 * Pure planner. Given the project's work items, the template stages and the
 * milestones just reached, returns the status updates to apply: complete every
 * stage whose milestoneKey was just reached. Blocked stages are never opened
 * here; sending the next selection to the customer is a deliberate action taken
 * from the ICH Homes dashboard (see startIchProjectStage).
 */
export function planIchProjectAdvance(workItems = [], options = {}) {
	const projectId = _.toString(options.projectId || "").trim();
	const stages = Array.isArray(_.get(options, ["template", "stages"])) ? options.template.stages : [];
	const reached = new Set(Array.isArray(options.reachedMilestoneKeys) ? options.reachedMilestoneKeys : []);
	if (!projectId || !stages.length || !reached.size) {
		return [];
	}
	const projectItems = (Array.isArray(workItems) ? workItems : [])
		.filter((workItem) => workItem && workItem.id && getWorkItemProjectId(workItem) === projectId);
	if (!projectItems.length) {
		return [];
	}
	const itemByStage = {};
	projectItems.forEach((workItem) => {
		const stageKey = getWorkItemStageKey(workItem);
		if (stageKey && !itemByStage[stageKey]) {
			itemByStage[stageKey] = workItem;
		}
	});
	const updates = [];
	stages.forEach((stage) => {
		const workItem = itemByStage[stage.key];
		if (!workItem) {
			return;
		}
		// The work item remembers the milestone its stage was bound to when the project
		// was created, so a customised org template still advances without being re-read.
		const milestoneKey = _.toString(stage.milestoneKey || _.get(workItem, ["metadata", "milestoneKey"]) || "").trim();
		const context = getProjectOpsFromWorkItem(workItem);
		const finalizationRequired = stage.completeOnAddendumFinalized || context.completeOnAddendumFinalized;
		const finalized = !finalizationRequired || (options.pricingContractProto?.documents || []).some(document => document
			&& document.kind === "addendum" && document.status === "locked" && Number(document.number) === Number(context.addendumNumber || stage.addendumNumber));
		if (finalized && milestoneKey && reached.has(milestoneKey) && !TERMINAL_WORK_ITEM_STATUSES.has(workItem.status)) {
			updates.push({
				workItemId: workItem.id,
				stageKey: stage.key,
				status: "completed",
				reason: `Milestone "${milestoneKey}" reached automatically.`
			});
		}
	});
	return updates;
}
